/**
 * Manifest review — the morning that turns yesterday's provider visit into
 * today's leads.
 *
 * Isaiah Cotto, 2026-09-23: "It is imperative we get the Manifest lead system
 * running to our operations so we can get submittals running again."
 *
 * == What was missing ====================================================
 *
 * The six routes have existed since 2026-09-21. The reviewer role has existed
 * since 2026-09-21. 33 buildings and 4,419 resident lines have been waiting.
 * What did not exist was a screen — so the whole lead origin was reachable
 * only by an engineer with a shell, and the distribution ring, which now
 * requires a manifest-confirmed lead, had nothing to distribute.
 *
 * == The shape of the work ===============================================
 *
 * Left: the buildings whose manifests are waiting, newest visit first, with
 * how much of each is decided. Right: the residents eMerge holds for the one
 * she opened, each a row with one decision on it.
 *
 * Undecided lines sort first, because the undecided ones are the work. A
 * decided line stays visible rather than disappearing — a reviewer must be
 * able to see and change what she recorded five minutes ago.
 *
 * == Two acts, kept apart ================================================
 *
 * Recording a decision decides nothing downstream. Only "Send to a Patient
 * Advocate" creates work for somebody, it is offered only on a confirmed
 * TWL, and it is gated by its own capability — a reviewer may hold the first
 * without the second, and when she does the button is absent rather than
 * broken.
 *
 * == A held lead is recorded, never discarded ============================
 *
 * When the eligibility engine holds a confirmed TWL, the intake exists, the
 * case exists, and this screen says which fact is missing. That is the
 * difference between somebody fixing it this morning and nobody knowing.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import {
  Disposition, DISPOSITION_LABEL, DISPOSITION_MEANS, DISPOSITION_TONE,
  FacilityOption, IntakeResult, ManifestIndex, ManifestRow, ResidentLine, ReviewDetail,
  TwlDayResult, manifestApi, reasonWords,
  EVIDENCE_LABEL, MondayReconciliation, MondayReconciliationItem, ProviderChoice,
} from './manifestApi';
import LeadDesk from './LeadDesk';
import ReplacementDesk from './ReplacementDesk';
import ResubmittalsDesk from './ResubmittalsDesk';
import VerificationDesk from './VerificationDesk';
import UnassignedDesk from './UnassignedDesk';
import { advocateApi, Scope } from '../advocate/advocateApi';
import { StateBlock as TideState } from '../../components/tide';
import { MfSkeleton, MfStateBlock, Refusal, UNREACHED, failureFor } from './mfStates';
// The shared workspace bridge first (Tide phase 2), then this workspace's own sheet.
import '../../styles/tide/workspace.css';
import './manifest.css';
import { resubmittalsApi } from './resubmittalsApi';
import { SendBackAction } from '../../components/sendBack/SendBackDialog';
import { sendBackPath } from '../../components/sendBack/sendBackApi';
import { StatusPaletteContext, ToneChip } from './board/chips';
import MfBoard, { useClientSort, useOpenParam, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ResidentName, residentLabel, residentName } from './board/residents';
import './manifests.board.css';

const BASE = '/portal/manifest';

const OFFLINE: Refusal = {
  title: 'eMerge could not be reached',
  body: 'Nothing was recorded. Check the connection and try again — no decision is lost by retrying.',
};

/**
 * Classify a read that did not succeed. A named gate keeps its own words; then
 * the status decides (2026-10 fix: a 500 or a dropped connection used to read
 * "That did not work ... eMerge refused the request"): unreached or 5xx is a
 * failure, 401/403 is a refusal the screen cannot name, anything else is the
 * server's own sentence.
 */
function refusalFor(code?: string | null, status?: number, message?: string): Refusal {
  if (code === 'EMERGE_PHI_ACCESS_DENIED') {
    return {
      title: 'You are not on the operator list',
      body: 'You hold the reviewer role, but the manifest names residents and this tenant keeps a separate list of who may see resident detail. Ask an administrator to add you.',
    };
  }
  if (code === 'DENY_NO_CAPABILITY' || code === 'FORBIDDEN') {
    return {
      title: 'This is not yours to review',
      body: 'Manifest review is granted by a ratified role. If you should hold it, an administrator can grant MANIFEST_REVIEWER.',
    };
  }
  const failed = failureFor({ status, code });
  if (failed) return failed;
  if (status === 401 || status === 403) {
    return {
      title: 'That did not work',
      body: 'eMerge refused the request and did not say why in a way this screen understands. Nothing was recorded.',
    };
  }
  return { title: 'Not loaded', body: message || 'eMerge answered without what this screen needs. Nothing was recorded.', failure: true, status };
}

const shortDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : iso;
};

const Skeleton = MfSkeleton;
const StateBlock = MfStateBlock;

const SORTERS: Record<string, (r: ManifestRow) => string | number | null> = {
  building: (r) => r.facility_name,
  visited: (r) => r.visit_date,
  provider: (r) => r.provider,
  twl: (r) => r.twl_confirmed,
};

/** A worklist row's key: building and day (a payload from before 2026-10-05 had only the Monday id). */
const rowKey = (row: ManifestRow): string => row.key || `${row.facility_id}:${row.visit_date}`;

/**
 * Monday's visits with a manifest and no eMerge event (MAN-1 reconciliation).
 * Not the worklist: a Monday date can be produced by a planned date passing,
 * so a row here proves no visit. A reviewer who confirms from the workbook
 * that the visit happened may open its review, which links the Monday item as
 * provenance only; the row then leaves this list.
 */
function MondayReconciliationCard({ lens, onOpen, busyKey }: { lens: MondayReconciliation; onOpen: (item: MondayReconciliationItem) => void; busyKey: string | null }) {
  const [show, setShow] = useState(false);
  if (!lens.items.length) return null;
  return (
    <section className="mf-card" aria-label={lens.label}>
      <div className="mf-card-head">
        <h2>{lens.label}</h2>
        <span className="mf-count">{lens.items.length}</span>
        <button type="button" className="mf-go ghost" onClick={() => setShow((x) => !x)} aria-expanded={show}>{show ? 'Hide' : 'Show'}</button>
      </div>
      {show && (
        <div className="mf-card-body">
          <div className="mf-note warn"><span>{lens.what}</span></div>
          <ul className="mf-buildings">
            {lens.items.map((item) => (
              <li key={item.key}>
                <div className="mf-building">
                  <strong>{item.facility_name}</strong>
                  <span className="mf-meta">
                    Monday says visited {shortDate(item.monday_visit_date)}
                    {item.monday_provider ? ` · ${item.monday_provider} (Monday)` : ''}
                  </span>
                  <span className="mf-progress">
                    <span className="mf-tag quiet">Monday reference</span>
                    {item.monday_manifest_url && <a className="mf-link" href={item.monday_manifest_url} target="_blank" rel="noopener noreferrer">Monday&apos;s workbook link</a>}
                    <button type="button" className="mf-go ghost" disabled={busyKey === item.key} onClick={() => onOpen(item)}>
                      Open a review (links this Monday item)
                    </button>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Name the provider who made this visit, when eMerge's visit record named
 * nobody. The provider is who a send-back reaches; without one it refuses.
 */
function NameProvider({ reviewId, onNamed }: { reviewId: string; onNamed: (name: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [choices, setChoices] = useState<ProviderChoice[] | null>(null);
  const [chosen, setChosen] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setOpen(true); setErr(null);
    try {
      const r = await manifestApi.providers();
      if (r && r.success && r.data) setChoices(r.data.items);
      else setErr((r && (r.error || r.message)) || 'The providers could not be loaded.');
    } catch { setErr('The providers could not be loaded.'); }
  };
  const save = async () => {
    if (!chosen) return;
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.setProvider(reviewId, chosen);
      if (r && r.success && r.data) { setOpen(false); onNamed(r.data.provider); }
      else setErr((r && (r.error || r.message)) || 'The provider was not recorded.');
    } catch { setErr('The provider was not recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="mf-note warn" role="note">
      <span>
        <strong>No provider is named for this visit.</strong>{' '}
        eMerge&apos;s visit record does not say who made it, so a resident cannot be sent back to the provider until you name them.{' '}
        {!open && <button type="button" className="mf-go ghost" onClick={start}>Name the provider</button>}
        {open && (
          <>
            <select aria-label="Provider who made this visit" value={chosen} onChange={(e) => setChosen(e.target.value)} disabled={!choices}>
              <option value="">{choices ? 'Choose the provider' : 'Loading…'}</option>
              {(choices || []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.job_title ? ` (${c.job_title})` : ''}</option>)}
            </select>{' '}
            <button type="button" className="mf-go" disabled={!chosen || busy} onClick={save}>Record</button>{' '}
            <button type="button" className="mf-go ghost" onClick={() => setOpen(false)}>Cancel</button>
          </>
        )}
        {err && <span role="alert"> {err}</span>}
      </span>
    </div>
  );
}

type ProgressKey = 'notstarted' | 'undecided' | 'decided' | 'closed';

const progressKey = (row: ManifestRow, closed: boolean): ProgressKey =>
  closed ? 'closed' : row.review_id === null ? 'notstarted' : row.undecided > 0 ? 'undecided' : 'decided';

/** How far a building's manifest is: the same chip in the table and in the legend. */
function ProgressChip({ row, closed }: { row: ManifestRow; closed: boolean }) {
  const k = progressKey(row, closed);
  if (k === 'closed') return <ToneChip tone="quiet">{row.reviewed_by ? `Closed by ${row.reviewed_by}` : 'Closed'}</ToneChip>;
  if (k === 'notstarted') return <ToneChip tone="quiet">Not started</ToneChip>;
  if (k === 'undecided') return <ToneChip tone="attn">{row.undecided} undecided</ToneChip>;
  return <ToneChip tone="ok">All decided</ToneChip>;
}

const PROGRESS_MEANS: Record<ProgressKey, { label: string; meaning: string }> = {
  notstarted: { label: 'Not started', meaning: 'eMerge recorded the visit; nobody has opened its review yet. Open the row, then press Open the review.' },
  undecided: { label: 'Undecided', meaning: 'The review is open and some residents have no decision yet. The chip says how many.' },
  decided: { label: 'All decided', meaning: 'Every resident has a decision. The review can be closed.' },
  closed: { label: 'Closed', meaning: 'The review was closed; its decisions are kept as recorded. Hidden until Show closed is on.' },
};

/**
 * One resident, with the decision on them.
 *
 * `busy` is per-line rather than per-screen: a reviewer working down a
 * manifest should not have the whole list freeze because one row is saving.
 */
function PersonRow({
  line, dispositions, canIntake, reviewOpen, busy, onDecide, onIntake, canSendBack = false, onSentBack,
}: {
  line: ResidentLine;
  dispositions: Disposition[];
  canIntake: boolean;
  reviewOpen: boolean;
  busy: boolean;
  onDecide: (d: Disposition) => void;
  onIntake: () => void;
  /** The server's `can_send_back`: the caller may return a line they decided to its provider, signed. */
  canSendBack?: boolean;
  onSentBack?: (toName: string) => void;
}) {
  const decided = line.disposition !== null;
  const confirmed = line.disposition === 'TWL_CONFIRMED';
  const intake = line.intake;
  const notNew = Boolean(line.episode && line.episode.verdict !== 'NEW_LEAD_OK');
  const held = intake && intake.readiness_state !== 'READY' && intake.reasons.length > 0;

  return (
    <li>
      <div className={`mf-person${decided ? ' decided' : ''}`}>
        <div>
          <div className="mf-name"><ResidentName p={line} /></div>
          <div className="mf-pfacts">
            {line.room && <span>Room {line.room}</span>}
            {line.date_of_birth && <span>DOB {shortDate(line.date_of_birth)}</span>}
            {line.counselear_id && <span>CE {line.counselear_id}</span>}
            {!line.patient_id && <span className="mf-tag stop">Identity unresolved</span>}
            {line.source_tags.length > 0 && (
              <span className="mf-tags">
                {line.source_tags.map((t) => <span key={t} className="mf-tag quiet" title="From CounselEAR. Context for your decision, not a verdict.">{t}</span>)}
              </span>
            )}
            {line.has_open_case && <span className="mf-tag attn" title="A Patient Advocate case is already open for this resident.">Open case</span>}
          </div>
          {notNew && <div className="mf-notes mf-episode" role="note"><strong>{line.episode!.line}.</strong> Not a new lead. If the provider saw them again for a replacement, record it on Replacements.</div>}
          {line.alerts && <div className="mf-notes"><strong>Alert:</strong> {line.alerts}</div>}
          {line.notes && <div className="mf-notes">{line.notes}</div>}
          {decided && line.decided_by && (
            <div className="mf-notes"><span className="mf-quiet">{DISPOSITION_LABEL[line.disposition!]} · {line.decided_by}</span></div>
          )}
        </div>

        <div className="mf-decide" role="group" aria-label={`Decision for ${residentLabel(line)}`}>
          {dispositions.map((d) => (
            <button
              key={d}
              type="button"
              className={DISPOSITION_TONE[d]}
              aria-pressed={line.disposition === d}
              disabled={busy || !reviewOpen || (notNew && d === 'TWL_CONFIRMED')}
              title={notNew && d === 'TWL_CONFIRMED' ? line.episode!.message || DISPOSITION_MEANS[d] : DISPOSITION_MEANS[d]}
              onClick={() => onDecide(d)}
            >
              {DISPOSITION_LABEL[d]}
            </button>
          ))}
        </div>

        {/* A decided line can be sent back for a retest. Undecided has no holder; the preview says so and the server decides again. */}
        {canSendBack && decided && (
          <div className="mf-intake">
            <SendBackAction
              path={sendBackPath.manifest(line.id)} recordLabel={residentLabel(line)} className="mf-go ghost" label="Send back to provider…"
              onClosed={(done) => { if (done) onSentBack?.(done.to.name); }}
            />
          </div>
        )}

        {confirmed && (
          <div className="mf-intake">
            {!intake && canIntake && (
              <button type="button" className="mf-go" disabled={busy || !line.patient_id || notNew} title={notNew ? line.episode!.message || undefined : undefined} onClick={onIntake}>
                Send to a Patient Advocate
              </button>
            )}
            {!intake && canIntake && !line.patient_id && (
              <span className="mf-quiet">eMerge cannot resolve who this is, so no case can be opened.</span>
            )}
            {!intake && !canIntake && (
              <span className="mf-quiet">Recorded. Creating the lead needs the intake capability, which you do not hold.</span>
            )}
            {intake && !held && <span className="mf-tag go">Lead created · ready for distribution</span>}
            {intake && held && (
              <div className="mf-held">
                <strong>Lead created, held out of distribution:</strong>
                <ul>{intake.reasons.map((r) => <li key={r}>{reasonWords(r)}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Record a day's TWL Q.
 *
 * Isaiah Cotto, 2026-09-28: "The TWLQ list is the tally of that days
 * manifests qualified company wide." Gwynn writes that tally at the end of
 * the day, and until now there was nowhere to put it: the Waiting list only
 * offers buildings eMerge already knew were visited, which on 2026-09-28 was
 * 7 of the 42 building-visits the tally actually recorded.
 *
 * So this names the building itself. Then it searches that building's census
 * rather than making her walk a hundred rows to find five — her tally is
 * three to seventeen names, and it is already written down.
 *
 * Nothing is sent until she presses Record. A half-entered day is worse than
 * an unentered one, because she cannot see which half landed.
 */
function TwlDayForm({ words, onRecorded, onClose }: {
  words: string[];
  onRecorded: (r: TwlDayResult, facility: string) => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState<'where' | 'who'>('where');
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<FacilityOption[]>([]);
  const [facility, setFacility] = useState<FacilityOption | null>(null);
  const [visitDate, setVisitDate] = useState('');
  const [provider, setProvider] = useState('');
  const [lines, setLines] = useState<ResidentLine[]>([]);
  const [find, setFind] = useState('');
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Today where the reviewer is, never toISOString — she is in Eastern time
  // and after 20:00 that would offer her tomorrow.
  const todayLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  useEffect(() => { if (!visitDate) setVisitDate(todayLocal()); }, [visitDate]);

  useEffect(() => {
    if (q.trim().length < 2) { setOptions([]); return undefined; }
    let live = true;
    const t = window.setTimeout(async () => {
      try {
        const r = await manifestApi.facilities(q.trim());
        if (live && r.success && r.data) setOptions(r.data.items);
      } catch { /* the picker simply offers nothing */ }
    }, 250);
    return () => { live = false; window.clearTimeout(t); };
  }, [q]);

  const openDay = async () => {
    if (!facility || !visitDate) return;
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.open(facility.id, visitDate);
      if (!r.success || !r.data) { setErr(r.error || 'That day could not be opened.'); return; }
      setLines(r.data.residents);
      setStep('who');
    } catch { setErr('Could not reach the server.'); } finally { setBusy(false); }
  };

  const record = async () => {
    if (!facility) return;
    const residents = Object.entries(picks).map(([patient_id, outcome]) => ({ patient_id, outcome }));
    if (!residents.length) { setErr('Nobody is on this day yet.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.recordDay({
        facility_id: facility.id, visit_date: visitDate,
        provider: provider.trim() || null, residents,
      });
      if (!r.success || !r.data) { setErr(r.error || 'That day was not recorded.'); return; }
      onRecorded(r.data, facility.name);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  const shown = lines.filter((l) => {
    if (!l.patient_id) return false;
    const t = find.trim().toLowerCase();
    if (!t) return Boolean(picks[l.patient_id]);
    return (residentName(l) || '').toLowerCase().includes(t) || String(l.room || '').toLowerCase().includes(t);
  });
  const chosen = Object.keys(picks).length;

  return (
    <section className="mf-card mf-twlday" aria-label="Record a day's TWL Q">
      <div className="mf-card-head">
        <h2>Record a day&rsquo;s TWL Q</h2>
        <button type="button" className="mf-go ghost" onClick={onClose}>Close</button>
      </div>

      {step === 'where' && (
        <div className="mf-form">
          <label><span>Building</span>
            {facility ? (
              <span className="mf-picked">
                {facility.name}
                <button type="button" className="mf-link" onClick={() => { setFacility(null); setQ(''); }}>Change</button>
              </span>
            ) : (
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Start typing the building's name" autoFocus />
            )}
          </label>
          {!facility && options.length > 0 && (
            <ul className="mf-options">
              {options.map((o) => (
                <li key={o.id}>
                  <button type="button" onClick={() => { setFacility(o); setOptions([]); }}>
                    {o.name}{o.city ? <span className="mf-quiet"> · {o.city}{o.state ? `, ${o.state}` : ''}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <label><span>Day of the visit</span>
            <input type="date" value={visitDate} max={todayLocal()} onChange={(e) => setVisitDate(e.target.value)} />
          </label>
          <label><span>Provider</span>
            <input value={provider} onChange={(e) => setProvider(e.target.value)} maxLength={120}
              placeholder="As the tally names them" />
          </label>
          {err && <p className="mf-note stop" role="alert">{err}</p>}
          <div className="mf-form-acts">
            <button type="button" className="mf-go" disabled={busy || !facility || !visitDate} onClick={openDay}>
              {busy ? 'Opening…' : 'Open the day'}
            </button>
          </div>
        </div>
      )}

      {step === 'who' && (
        <div className="mf-form">
          <p className="mf-sub">
            {facility?.name} · {shortDate(visitDate)}{provider ? ` · ${provider}` : ''} ·{' '}
            {lines.length} {lines.length === 1 ? 'resident' : 'residents'} on the census
          </p>
          <label><span>Find a resident</span>
            <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Name or room" autoFocus />
          </label>

          {find.trim() && shown.length === 0 && (
            <TideState kind="empty" reason="filtered" message={<>Nobody on this building&rsquo;s census matches that.</>} />
          )}
          {!find.trim() && chosen === 0 && (
            <p className="mf-empty">Search for each resident on your tally and give them the word you wrote down.</p>
          )}

          <ul className="mf-picks">
            {shown.map((l) => (
              <li key={l.id}>
                <div className="mf-name"><ResidentName p={l} />{l.room ? <span className="mf-quiet"> · Room {l.room}</span> : null}</div>
                <div className="mf-decide" role="group" aria-label={`What the tally says for ${residentLabel(l)}`}>
                  {words.map((w) => (
                    <button key={w} type="button"
                      className={picks[l.patient_id!] === w ? 'go' : 'quiet'}
                      aria-pressed={picks[l.patient_id!] === w}
                      onClick={() => setPicks((p) => {
                        const next = { ...p };
                        if (next[l.patient_id!] === w) delete next[l.patient_id!];
                        else next[l.patient_id!] = w;
                        return next;
                      })}>
                      {w}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>

          {err && <p className="mf-note stop" role="alert">{err}</p>}
          <div className="mf-form-acts">
            <button type="button" className="mf-go" disabled={busy || chosen === 0} onClick={record}>
              {busy ? 'Recording…' : `Record the day · ${chosen}`}
            </button>
            <button type="button" className="mf-go ghost" disabled={busy} onClick={() => setStep('where')}>Back</button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function ManifestShell() {
  const [params, setParams] = useSearchParams();
  // The open review lives in the URL (?review=<id>), so a link or a refresh lands on it.
  const [reviewId, setReview] = useOpenParam('review', { push: true });
  // The desk's views (2026-09-30), each its own link: ?view=leads, ?view=coverage, ?view=replacements.
  // And Unassigned, moved here from the Patient Advocate workspace the same day: ?view=unassigned.
  // 'verification' (2026-10-02): leads a VA checks benefits for, after Resubmittals.
  // 'resubmittals' (2026-10-01): residents a PA sent back to the provider, after Replacements.
  const DESK_VIEWS = ['leads', 'coverage', 'replacements', 'unassigned', 'resubmittals', 'verification'] as const;
  type DeskView = typeof DESK_VIEWS[number];
  const rawView = params.get('view');
  const deskView = (DESK_VIEWS as readonly string[]).includes(rawView || '') ? rawView as DeskView : null;
  const goToView = (v: DeskView | null, replace = false) => {
    const next = new URLSearchParams();
    if (v) next.set('view', v);
    setParams(next, { replace });
  };

  /*
   * Unassigned is shown to whoever the server lets place PA work
   * (ADVOCACY_TEAM_MANAGE, read from the advocate workspace's own scope),
   * not to every reviewer: a reviewer who is not a PA manager would get a
   * tab whose every request is refused. The server decides again on every call.
   */
  const [paScope, setPaScope] = useState<Scope | null>(null);
  const [paScopeKnown, setPaScopeKnown] = useState(false);
  const loadPaScope = useCallback(async () => {
    try {
      const r = await advocateApi.scope();
      setPaScope(r.success && r.data ? r.data : null);
    } catch { setPaScope(null); } finally { setPaScopeKnown(true); }
  }, []);
  useEffect(() => { loadPaScope(); }, [loadPaScope]);
  const placesPaWork = Boolean(paScope?.manager);
  // A hand-typed ?view=unassigned from someone who cannot place work lands on the manifests.
  useEffect(() => {
    if (deskView === 'unassigned' && paScopeKnown && !placesPaWork) goToView(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deskView, paScopeKnown, placesPaWork]);

  // The send-back flag lives on the resubmittals read (design §4.1); a failed read is "no flag", never a reason to offer the act.
  const [canSendBack, setCanSendBack] = useState(false);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await resubmittalsApi.list();
        if (live) setCanSendBack(Boolean(r.success && r.data && r.data.can_send_back));
      } catch { if (live) setCanSendBack(false); }
    })();
    return () => { live = false; };
  }, []);

  const [index, setIndex] = useState<ManifestIndex | null>(null);
  const [indexLoading, setIndexLoading] = useState(true);
  const [indexRefusal, setIndexRefusal] = useState<Refusal | null>(null);

  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRefusal, setDetailRefusal] = useState<Refusal | null>(null);

  const [busyLine, setBusyLine] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [legendKey, setLegendKey] = useState<string | null>(null);
  // A row with no review yet has no id to put in the URL: it opens a panel that offers to start one.
  const [unstartedKey, setUnstartedKey] = useState<string | null>(null);
  /** The Not started row whose "Open the review" was refused, so its panel says why. */
  const [refusedKey, setRefusedKey] = useState<string | null>(null);
  const [showTwlDay, setShowTwlDay] = useState(false);

  const loadIndex = useCallback(async () => {
    setIndexLoading(true); setIndexRefusal(null);
    try {
      const r = await manifestApi.index();
      if (r.success && r.data) setIndex(r.data);
      else setIndexRefusal(refusalFor(r.code, r.status, r.message));
    } catch { setIndexRefusal(UNREACHED); } finally { setIndexLoading(false); }
  }, []);
  useEffect(() => { loadIndex(); }, [loadIndex]);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true); setDetailRefusal(null);
    try {
      const r = await manifestApi.detail(id);
      if (r.success && r.data) setDetail(r.data);
      else {
        setDetail(null);
        setDetailRefusal(r.code === 'NOT_FOUND'
          ? { title: 'No such review here', body: 'This review does not exist, or it belongs to a building outside your scope. Those are deliberately the same answer.' }
          : refusalFor(r.code, r.status, r.message));
      }
    } catch { setDetail(null); setDetailRefusal(UNREACHED); } finally { setDetailLoading(false); }
  }, []);

  useEffect(() => { setNotice(null); setProblem(null); }, [reviewId]);
  useEffect(() => { if (reviewId) loadDetail(reviewId); else setDetail(null); }, [reviewId, loadDetail]);

  const goToReview = (id: string | null) => { setReview(id); };

  /**
   * Open a building's morning. Idempotent on the server by (building, visit
   * date), so pressing it on a review already in progress joins that one
   * rather than starting a second.
   */
  const openBuilding = async (row: ManifestRow) => {
    setProblem(null);
    if (row.review_id) { goToReview(row.review_id); return; }
    // A refusal is said where it was asked for: in this row's Not started panel (dc79e92 drew it in the detail pane).
    setDetailRefusal(null); setRefusedKey(rowKey(row));
    setDetailLoading(true);
    try {
      const r = await manifestApi.open(row.facility_id, row.visit_date, { visitPlanId: row.visit_plan_id || null, checkInId: row.check_in_id || null });
      if (r.success && r.data) {
        setDetail(r.data);
        setUnstartedKey(null);
        goToReview(r.data.review.id);
        loadIndex();
      } else { setDetailRefusal(refusalFor(r.code, r.status, r.message)); }
    } catch { setDetailRefusal(UNREACHED); } finally { setDetailLoading(false); }
  };

  /** Open a review for a Monday-only visit, linking the Monday item as provenance (MAN-2a). */
  const [busyMondayKey, setBusyMondayKey] = useState<string | null>(null);
  const openFromMonday = async (item: MondayReconciliationItem) => {
    setProblem(null); setBusyMondayKey(item.key);
    try {
      const r = await manifestApi.open(item.facility_id, item.monday_visit_date, { schedulingId: item.scheduling_id });
      if (r.success && r.data) {
        setDetail(r.data);
        goToReview(r.data.review.id);
        loadIndex();
      } else setProblem(r.error || r.message || 'That review could not be opened.');
    } catch { setProblem(OFFLINE.body); } finally { setBusyMondayKey(null); }
  };

  const decide = async (line: ResidentLine, d: Disposition) => {
    if (!detail) return;
    setBusyLine(line.id); setProblem(null);
    try {
      const r = await manifestApi.decide(detail.review.id, line.id, d);
      if (r.success) await loadDetail(detail.review.id);
      else setProblem(r.error || r.message || 'That decision was not recorded.');
    } catch { setProblem(OFFLINE.body); } finally { setBusyLine(null); }
  };

  const sendToAdvocate = async (line: ResidentLine) => {
    if (!detail) return;
    setBusyLine(line.id); setProblem(null);
    try {
      const r = await manifestApi.addToIntake(detail.review.id, line.id);
      if (r.success && r.data) {
        const out = r.data as IntakeResult;
        setNotice(out.readiness_state === 'READY'
          ? `${residentLabel(line)} is now a lead, ready for distribution.`
          : `${residentLabel(line)} is now a lead, held until ${out.reasons.map(reasonWords).join(' and ')}.`);
        await loadDetail(detail.review.id);
        loadIndex();
      } else { setProblem(r.error || r.message || 'The lead was not created.'); }
    } catch { setProblem(OFFLINE.body); } finally { setBusyLine(null); }
  };

  const closeMorning = async () => {
    if (!detail) return;
    setProblem(null);
    try {
      const r = await manifestApi.complete(detail.review.id);
      if (r.success) { setNotice('This manifest is closed.'); await loadDetail(detail.review.id); loadIndex(); }
      else setProblem(r.error || r.message || 'This review was not closed.');
    } catch { setProblem(OFFLINE.body); }
  };

  const dispositions = index?.dispositions ?? [];
  const canIntake = Boolean(index?.can.create_intake);
  const waiting = useMemo(() => index?.waiting ?? [], [index]);
  const completed = useMemo(() => index?.completed ?? [], [index]);

  const totals = useMemo(() => ({
    buildings: waiting.length,
    undecided: waiting.reduce((n, r) => n + r.undecided, 0),
  }), [waiting]);


  // The review in the URL, once its detail has loaded: which building-visit row it belongs to.
  const detailFor = detail && reviewId && detail.review.id === reviewId ? detail : null;
  const matches = (r: ManifestRow): boolean => Boolean(reviewId) && (
    r.review_id === reviewId
    || Boolean(detailFor && r.facility_id === detailFor.review.facility_id && r.visit_date === detailFor.review.visit_date)
  );
  // A review the index does not list (a hand-typed link, a review opened from a day's tally) still gets its row, so its panel can open.
  const listed = waiting.some(matches) || completed.some(matches);
  const synthetic: ManifestRow | null = detailFor && !listed ? {
    key: `${detailFor.review.facility_id}:${detailFor.review.visit_date}`,
    facility_id: detailFor.review.facility_id, facility_name: detailFor.review.facility_name, visit_date: detailFor.review.visit_date,
    manifest_url: detailFor.review.manifest_url, provider: detailFor.review.provider, scheduling_id: detailFor.review.scheduling_id ?? null,
    review_id: detailFor.review.id, lines_total: detailFor.counts.lines, twl_confirmed: detailFor.counts.twl_confirmed,
    undecided: detailFor.counts.undecided, reviewed_by: detailFor.review.completed_by, reviewed_at: detailFor.review.completed_at,
  } : null;
  const syntheticClosed = Boolean(synthetic && detailFor && detailFor.review.state === 'COMPLETED');
  const closedKeys = new Set(completed.map(rowKey));
  if (synthetic && syntheticClosed) closedKeys.add(rowKey(synthetic));
  const isClosed = (r: ManifestRow) => closedKeys.has(rowKey(r));
  const includeClosed = showDone || legendKey === 'closed' || completed.some(matches);
  // The table holds the waiting visits, and the closed ones when asked for; its count is these rows.
  const baseRows: ManifestRow[] = [...(synthetic ? [synthetic] : []), ...waiting, ...(includeClosed ? completed : [])];
  const shown = legendKey ? baseRows.filter((r) => progressKey(r, isClosed(r)) === legendKey) : baseRows;
  const sorted = useClientSort(shown, SORTERS, { sort: null });

  const legendItems: MfLegendItem[] = (['notstarted', 'undecided', 'decided', 'closed'] as ProgressKey[]).flatMap((k) => {
    const mine = baseRows.filter((r) => progressKey(r, isClosed(r)) === k);
    const hidden = k === 'closed' && mine.length === 0 && completed.length > 0;
    const sample = hidden ? completed[0] : mine[0];
    if (!sample) return [];
    return [{
      key: k, label: PROGRESS_MEANS[k].label, sample: <ProgressChip row={sample} closed={k === 'closed'} />,
      meaning: PROGRESS_MEANS[k].meaning, count: hidden ? completed.length : mine.length,
    }];
  });

  const openRowMatch = reviewId ? baseRows.find(matches) : undefined;
  const openKey = openRowMatch ? rowKey(openRowMatch) : (unstartedKey && baseRows.some((r) => rowKey(r) === unstartedKey) ? unstartedKey : null);

  const onOpenChange = (key: string | null) => {
    setProblem(null); setDetailRefusal(null);
    if (!key) { setUnstartedKey(null); goToReview(null); return; }
    const row = baseRows.find((r) => rowKey(r) === key);
    if (!row) return;
    if (row.review_id) { setUnstartedKey(null); goToReview(row.review_id); } else { setUnstartedKey(key); goToReview(null); }
  };

  const columns: BoardColumn<ManifestRow>[] = [
    { key: 'visited', label: 'Visited', sort: 'visited', help: 'The day eMerge recorded the visit. Newest first until you sort.', render: (r) => <span>{shortDate(r.visit_date)}</span> },
    {
      key: 'provider', label: 'Provider', sort: 'provider', help: "Who made the visit, from eMerge's visit record. When two providers were planned that day nobody is guessed: name one in the review.",
      render: (r) => (
        <span className="mf-chips">
          {r.provider ? <span className="board-clip" title={r.provider}>{r.provider}</span> : <span className="board-na">not named</span>}
          {r.provider_ambiguous && <ToneChip tone="attn">Two providers planned: name one</ToneChip>}
        </span>
      ),
    },
    {
      key: 'evidence', label: 'Evidence', help: 'What eMerge holds that says the visit happened.',
      render: (r) => {
        const words = (r.evidence || []).map((e) => EVIDENCE_LABEL[e]).join(', ');
        return words ? <span className="board-clip" title={words}>{words}</span> : <span className="board-na">not recorded</span>;
      },
    },
    { key: 'progress', label: 'Progress', help: 'How far the review is: not started, how many residents are undecided, all decided, or closed and by whom.', render: (r) => <ProgressChip row={r} closed={isClosed(r)} /> },
    { key: 'twl', label: 'TWL confirmed', numeric: true, sort: 'twl', help: 'Residents a reviewer confirmed as a TWL on this manifest.', render: (r) => <span>{r.twl_confirmed}</span> },
  ];

  const identity = {
    label: 'Building',
    sort: 'building',
    help: 'The building whose manifest this is. Open the row to review its residents, docked beside the table.',
    render: (r: ManifestRow) => <strong>{r.facility_name}</strong>,
    name: (r: ManifestRow) => r.facility_name,
  };

  const sections = (row: ManifestRow): BoardDetailSection[] => {
    const reviewing = Boolean(reviewId) && matches(row);
    if (!reviewing && !row.review_id) {
      return [{
        id: 'review', label: 'Review',
        render: () => (
          <div className="mf-panel">
            <p><ToneChip tone="quiet">Not started</ToneChip></p>
            <p>eMerge recorded this visit; nobody has opened its review yet. Nothing is created until you press the button.</p>
            <div className="mf-acts">
              <button type="button" className="board-btn board-btn--primary" disabled={detailLoading} onClick={() => openBuilding(row)}>Open the review</button>
            </div>
            {/* The refusal, in the same words the review pane used (StateBlock's error is role="alert"). */}
            {detailRefusal && refusedKey === rowKey(row) && <StateBlock refusal={detailRefusal} retry={() => openBuilding(row)} />}
          </div>
        ),
      }];
    }
    if (detailRefusal) {
      return [{ id: 'residents', label: 'Residents', render: () => <StateBlock refusal={detailRefusal} retry={reviewId ? () => loadDetail(reviewId) : undefined} /> }];
    }
    const d = detailFor;
    if (!d) return [{ id: 'residents', label: 'Residents', render: () => <Skeleton /> }];
    const out: BoardDetailSection[] = [{
      id: 'residents', label: 'Residents',
      render: () => (
        <div className="mf-panel">
          <p className="mf-quiet">
            {d.counts.lines} {d.counts.lines === 1 ? 'resident' : 'residents'}
            {d.counts.undecided > 0 ? ` · ${d.counts.undecided} undecided` : ''}
            {d.counts.twl_confirmed > 0 ? ` · ${d.counts.twl_confirmed} confirmed TWL · ${d.counts.intakes} sent` : ''}
          </p>

          {!d.review.snapshot_imported && (
            <div className="mf-note warn">
              <span>
                <strong>These lines are eMerge&apos;s own residents for this building, not the workbook.</strong>{' '}
                The file itself has not been read into this review, so a resident the provider wrote onto the sheet
                who is not in eMerge will not appear below.
                {d.review.manifest_url && <> Open the manifest alongside this list: </>}
                {d.review.manifest_url && (
                  <a className="mf-link" href={d.review.manifest_url} target="_blank" rel="noopener noreferrer">the workbook</a>
                )}
              </span>
            </div>
          )}

          {d.review.provider_employee_id === null && (
            <NameProvider
              reviewId={d.review.id}
              onNamed={(name) => { setNotice(`${name || 'The provider'} is recorded as this visit's provider.`); loadDetail(d.review.id); loadIndex(); }}
            />
          )}

          {d.review.state === 'COMPLETED' && (
            <div className="mf-note">
              <span>
                <strong>Closed{d.review.completed_by ? ` by ${d.review.completed_by}` : ''}.</strong>{' '}
                Decisions are kept as they were recorded.
              </span>
            </div>
          )}

          {d.residents.length === 0 ? (
            <TideState kind="empty" reason="none" message="eMerge holds no confirmed residents for this building, so there is nothing to decide against the manifest." />
          ) : (
            <ul className="mf-rows">
              {d.residents.map((line) => (
                <PersonRow
                  key={line.id}
                  line={line}
                  dispositions={dispositions}
                  canIntake={canIntake}
                  reviewOpen={d.review.state === 'OPEN'}
                  busy={busyLine === line.id}
                  onDecide={(x) => decide(line, x)}
                  onIntake={() => sendToAdvocate(line)}
                  canSendBack={canSendBack}
                  onSentBack={(name) => { setNotice(`${residentLabel(line)} was sent back to ${name}.`); loadDetail(d.review.id); }}
                />
              ))}
            </ul>
          )}
        </div>
      ),
    }];
    if (d.review.state === 'OPEN') {
      out.push({
        id: 'close', label: 'Close',
        render: () => (
          <div className="mf-panel">
            <div className="mf-acts">
              <button type="button" className="board-btn board-btn--primary" disabled={d.counts.undecided > 0} onClick={closeMorning}>
                Close this manifest
              </button>
            </div>
            {d.counts.undecided > 0 && (
              <p className="mf-quiet">
                {d.counts.undecided} {d.counts.undecided === 1 ? 'resident has' : 'residents have'} no decision yet.
                &quot;Needs review&quot; is a decision — it says you looked.
              </p>
            )}
          </div>
        ),
      });
    }
    return out;
  };

  const controls = completed.length > 0 ? (
    <button type="button" className={`board-btn${showDone ? ' board-btn--primary' : ''}`} aria-pressed={showDone} onClick={() => setShowDone((x) => !x)}>
      Show closed ({completed.length})
    </button>
  ) : null;

  let state: React.ReactNode = null;
  if (indexRefusal) state = <StateBlock refusal={indexRefusal} retry={loadIndex} />;
  else if (indexLoading && !index) state = <Skeleton />;
  else if (index && baseRows.length === 0) {
    state = <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="Nothing is waiting. A building appears here once eMerge records the visit: Scheduling completes the visit plan, or the provider checks in." />;
  }

  // A review that has no row to dock into (a refusal, or still loading) is said above the table.
  const rowless = !openRowMatch && !(unstartedKey && openKey === unstartedKey);

  return (
    <StatusPaletteContext.Provider value={paScope?.status_legend || []}>
    <div className="mf">
      <WorkspaceMenu current="MANIFEST" />
      <header className="mf-bar">
        <div className="mf-bar-inner">
          <div className="mf-ident">
            <div className="mf-where">
              <span className="mf-eyebrow">eMerge · Manifest review</span>
              <h1>{detail ? detail.review.facility_name : 'This morning'}</h1>
              <span className="mf-sub">
                {detail
                  ? `Visited ${shortDate(detail.review.visit_date)}${detail.review.provider ? ` · ${detail.review.provider}` : ''}`
                  : `${totals.buildings} ${totals.buildings === 1 ? 'manifest' : 'manifests'} waiting`}
              </span>
            </div>
            <div className="mf-bar-acts">
              {/* The end-of-day tally. Offered only to somebody who may create
                  a lead, because recording a day creates work for an advocate. */}
              {!detail && index?.can.create_intake && (
                <button type="button" className={`mf-go${showTwlDay ? ' ghost' : ''}`} onClick={() => setShowTwlDay((s) => !s)}>
                  {showTwlDay ? 'Close' : "Record a day's TWL Q"}
                </button>
              )}
              {detail && (
                <button type="button" className="mf-go ghost" onClick={() => { setUnstartedKey(null); goToReview(null); }}>
                  ← All manifests
                </button>
              )}
            </div>
          </div>

          {/* The desk's views, as links in the bar (Tide phase 2): the same idiom
              as the advocate's and the provider's tabs. Each is the URL it always
              was (?view=leads, ?view=coverage, ...), so a link can be opened,
              shared and returned to; the Manifests tab drops ?view and ?review,
              as the button it replaces did. */}
          <nav className="mf-tabs" aria-label="Manifest review">
            <Link to={BASE} className={`mf-tab${!deskView ? ' on' : ''}`} aria-current={!deskView ? 'page' : undefined}>Manifests</Link>
            <Link to={`${BASE}?view=leads`} className={`mf-tab${deskView === 'leads' ? ' on' : ''}`} aria-current={deskView === 'leads' ? 'page' : undefined}>Leads to place</Link>
            {placesPaWork && (
              <Link to={`${BASE}?view=unassigned`} className={`mf-tab${deskView === 'unassigned' ? ' on' : ''}`} aria-current={deskView === 'unassigned' ? 'page' : undefined}>Unassigned{paScope?.counts.unassigned !== undefined && <span className="mf-count">{paScope.counts.unassigned}</span>}</Link>
            )}
            <Link to={`${BASE}?view=replacements`} className={`mf-tab${deskView === 'replacements' ? ' on' : ''}`} aria-current={deskView === 'replacements' ? 'page' : undefined}>Replacements</Link>
            <Link to={`${BASE}?view=resubmittals`} className={`mf-tab${deskView === 'resubmittals' ? ' on' : ''}`} aria-current={deskView === 'resubmittals' ? 'page' : undefined}>Resubmittals</Link>
            <Link to={`${BASE}?view=verification`} className={`mf-tab${deskView === 'verification' ? ' on' : ''}`} aria-current={deskView === 'verification' ? 'page' : undefined}>Needs verification</Link>
            <Link to={`${BASE}?view=coverage`} className={`mf-tab${deskView === 'coverage' ? ' on' : ''}`} aria-current={deskView === 'coverage' ? 'page' : undefined}>Facility coverage</Link>
          </nav>
        </div>
      </header>

      {/* Each desk draws its own <main> and board. */}
      {(deskView === 'leads' || deskView === 'coverage') && <LeadDesk view={deskView} />}
      {deskView === 'replacements' && <ReplacementDesk />}
      {deskView === 'resubmittals' && <ResubmittalsDesk />}
      {deskView === 'verification' && <VerificationDesk />}

      {deskView === 'unassigned' && (placesPaWork && paScope
        ? <UnassignedDesk scope={paScope} onScopeStale={loadPaScope} />
        : <main className="mf-main mf-main--board"><Skeleton /></main>)}

      {!deskView && (
      <main className="mf-main mf-main--board">
        {showTwlDay && !detail && index && (
          <TwlDayForm
            words={index.twl_outcomes || []}
            onClose={() => setShowTwlDay(false)}
            onRecorded={(r, where) => {
              setShowTwlDay(false);
              setNotice(
                `${where}: ${r.recorded} recorded · ${r.leadsCreated} ${r.leadsCreated === 1 ? 'lead' : 'leads'} created`
                + (r.ready ? `, ${r.ready} ready for distribution` : '')
                + (r.held ? `, ${r.held} held pending evidence` : '')
                + (r.notLeads ? `, ${r.notLeads} recorded but not a lead` : '')
                + (r.refused ? `, ${r.refused} refused` : '') + '.'
              );
              loadIndex();
              // Deliberately does NOT open the review it just recorded.
              // Opening one changes `reviewId`, and the effect that clears
              // the notice on navigation would wipe the summary she just
              // asked for. She also has the next building on her tally to
              // enter, not one review to admire.
            }}
          />
        )}

        {!indexRefusal && index?.reconciliation && (
          <MondayReconciliationCard lens={index.reconciliation} onOpen={openFromMonday} busyKey={busyMondayKey} />
        )}

        {rowless && reviewId && detailRefusal && <StateBlock refusal={detailRefusal} retry={() => loadDetail(reviewId)} />}
        {rowless && reviewId && !detailRefusal && detailLoading && !detail && <Skeleton />}

        <MfBoard<ManifestRow>
          title="Manifests"
          count={index ? baseRows.length : null}
          units={['manifest', 'manifests']}
          what={<>Buildings whose visit eMerge recorded, newest first. Open a row to review its residents, docked beside the table.{totals.undecided > 0 ? ` ${totals.undecided} residents undecided on the waiting manifests.` : ''}</>}
          notice={(notice || problem) ? (
            <>
              {notice && <p className="mf-notice" role="status">{notice}</p>}
              {problem && <p className="mf-notice mf-notice--stop" role="alert">{problem}</p>}
            </>
          ) : null}
          controls={controls}
          legend={index && baseRows.length > 0 ? (
            <MfLegend title="Progress key" items={legendItems} whole={baseRows.length} wholeWords="in the table" units={['manifest', 'manifests']}
              value={legendKey} onSelect={setLegendKey}
              scope="Each chip is the progress word a row draws. Press an entry to show only those manifests; Closed shows the closed ones even while they are hidden." />
          ) : null}
          state={state}
          columns={columns}
          identity={identity}
          rows={sorted.rows}
          rowKey={rowKey}
          openKey={openKey}
          onOpenChange={onOpenChange}
          detailSections={sections}
          detailLabel={(r) => `${r.facility_name}: the manifest review`}
          sort={sorted.sort}
          dir={sorted.dir}
          sorts={sorted.sorts}
          onSort={sorted.onSort}
        />
      </main>
      )}
    </div>
    </StatusPaletteContext.Provider>
  );
}
